import { usersCurrentUserAuthUsersMeGetOptions, usersPatchCurrentUserAuthUsersMePatchMutation } from '@/client/@tanstack/react-query.gen'
import { UnsavedBadge } from '@/components/common/unsaved-badge'
import { UnsavedChangesBlocker } from '@/components/common/unsaved-changes-blocker'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Check, Loader2 } from 'lucide-react'
import { SettingsPageSkeleton } from './-components/skeleton'

export const Route = createFileRoute('/_authed/_user/settings/email-preferences')({
  component: EmailPreferencesPage,
  pendingComponent: SettingsPageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Email Preferences',
    getDescription: () => 'Customize how SUPPLYCO\'s AI drafts your outreach emails',
  }),
})

function EmailPreferencesPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    ...usersCurrentUserAuthUsersMeGetOptions(),
  })

  const { mutateAsync: updateProfile, isPending } = useMutation({
    ...usersPatchCurrentUserAuthUsersMePatchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: usersCurrentUserAuthUsersMeGetOptions().queryKey,
      })
      toast({ title: 'Saved', variant: 'success' })
    },
    onError: error_ =>
      toast({
        title: 'Failed to save',
        description: error_ instanceof Error ? error_.message : 'An unexpected error occurred',
        variant: 'destructive',
      }),
  })

  const form = useForm({
    defaultValues: profileData,
    onSubmit: async ({ value }) => {
      if (!value)
        return
      const response = await updateProfile({
        body: {
          email_system_prompt: value.email_system_prompt || '',
        },
      })
      form.reset(response)
    },
  })

  if (profileLoading) {
    return <SettingsPageSkeleton />
  }

  return (
    <>
      {!!profileError && (
        <Alert variant="destructive">
          <AlertDescription>{profileError.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start">
            <div className="flex-1">
              <CardTitle>AI Email Preferences</CardTitle>
              <CardDescription className="mt-1.5">
                Customize how SUPPLYCO&apos;s AI drafts your outreach emails.
              </CardDescription>
            </div>
            <form.Subscribe selector={(state: { fieldMeta: Record<string, { isDirty?: boolean }> }) =>
              state.fieldMeta['email_system_prompt']?.isDirty} // eslint-disable-line dot-notation -- TS4111: index signature requires bracket access
            >
              {isDirty => (isDirty ? <UnsavedBadge /> : undefined)}
            </form.Subscribe>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="emailPrompt">Email Draft Instructions</Label>
            <form.Field name="email_system_prompt">
              {field => (
                <>
                  <Textarea
                    id="emailPrompt"
                    value={(field.state.value as string) || ''}
                    placeholder="Example: I work at Acme Corp, a software company specializing in cloud infrastructure..."
                    className="min-h-30 resize-y"
                    disabled={isPending}
                    maxLength={5000}
                    onChange={event_ => field.handleChange(event_.target.value)}
                  />
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                      Describe your tone, company focus, and key talking points
                    </p>
                    <p className="text-sm text-muted-foreground tabular-nums">
                      {(field.state.value as string | undefined)?.length ?? 0}
                      {' '}
                      / 5000
                    </p>
                  </div>
                </>
              )}
            </form.Field>
          </div>

          <form.Subscribe selector={(state: { fieldMeta: Record<string, { isDirty?: boolean }> }) =>
            state.fieldMeta['email_system_prompt']?.isDirty} // eslint-disable-line dot-notation -- TS4111: index signature requires bracket access
          >
            {isDirty => isDirty && (
              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  disabled={isPending}
                  onClick={() => form.handleSubmit()}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            )}
          </form.Subscribe>
        </CardContent>
      </Card>

      <form.Subscribe selector={(state: { isDirty: boolean }) => state.isDirty}>
        {isDirty => <UnsavedChangesBlocker isDirty={isDirty} />}
      </form.Subscribe>
    </>
  )
}
